/**
 * @template T
 * @typedef {Object} StateInstance
 * @property {T} value - The current state value
 * @property {Array<T>} history - Array of previous state values for undo functionality
 * @property {Array<T>} future - Array of future state values for redo functionality
 * @property {Set<function(T): void>} listeners - Set of subscriber callback functions
 * @property {boolean} canUndo - Whether undo operation is possible
 * @property {boolean} canRedo - Whether redo operation is possible
 * @property {function(function(T): void): function(): void} subscribe - Subscribe to state changes
 * @property {function(): Promise<Array>} notifyListeners - Notify all listeners of state changes
 * @property {function(): Promise<Array>} clear - Reset state to initial value
 * @property {function(): Promise<Array>} undo - Undo last state change
 * @property {function(): Promise<Array>} redo - Redo last undone change
 */

/**
 * Creates a reactive state management instance with dynamic getter/setter methods.
 * This is the base state factory that all other states should inherit from.
 *
 * @template T
 * @param {string} key - The name of the getter method (e.g., 'user' creates a user property)
 * @param {string} setKey - The name of the setter method (e.g., 'setUser' creates a setUser() method)
 * @param {T} initialValue - The initial value of the state (e.g., null, [], {}, etc.)
 *
 * @returns {StateInstance<T>} A state instance with dynamic getter/setter properties
 *
 * Example Usage:
 * ```javascript
 * // Create a new state instance
 * // null is the initial value and can be changed to any other value
 * const UserState = _State('user', 'setUser', null);
 *
 * // Get the current value
 * const user = UserState.user;
 *
 * // Set a new value
 * await UserState.setUser({ id: 1, name: 'John' });
 *
 * // Subscribe to changes
 * const unsubscribe = UserState.subscribe(newValue => {
 *     console.log('User updated:', newValue);
 * });
 *
 * // Cleanup subscription when done
 * unsubscribe();
 *
 * // Reset to initial value
 * await UserState.clear();
 * ```
 */
const _State = (key, setKey, initialValue) => {
  // Helper for deep cloning: arrays and plain objects only. Class instances (like THREE.Scene) are returned as-is.
  const deepClone = (val, seen = new WeakMap()) => {
    if (val && typeof val === 'object') {
      if (seen.has(val)) {
        return seen.get(val);
      }

      if (Array.isArray(val)) {
        const newArr = [];
        seen.set(val, newArr);
        for (let i = 0; i < val.length; i++) {
          newArr[i] = deepClone(val[i], seen);
        }
        return newArr;
      }

      if (val.constructor === Object) {
        const newObj = {};
        seen.set(val, newObj);
        for (const k in val) {
          if (Object.prototype.hasOwnProperty.call(val, k)) {
            newObj[k] = deepClone(val[k], seen);
          }
        }
        return newObj;
      }
      return val;
    }
    return val;
  };

  const historyLimit = 10;
  const state = {
    /**
     * The current value of the state
     * @type {*}
     */
    value: deepClone(initialValue),
    history: [], // stores previous values for undo
    future: [], // stores values for redo

    /**
     * Set of subscriber callback functions
     * @type {Set<Function>}
     */
    listeners: new Set(),

    /**
     * Dynamic getter property created based on the 'key' parameter
     * @returns {T} Current state value
     */
    get [key]() {
      return this.value;
    },

    /**
     * Dynamic setter method created based on the 'setKey' parameter
     * Updates the state value and notifies all listeners
     * @param {T} value - New state value
     * @returns {Promise<Array>} Promise that resolves to the listener results
     */
    [setKey]: function (value) {
      // Push current value to history before updating
      this.history.push(deepClone(this.value));
      if (this.history.length > historyLimit) this.history.shift();
      this.value = deepClone(value);
      // Clear redo stack on new set
      this.future = [];
      return this.notifyListeners();
    },

    /**
     * Undo the last state change (if possible)
     * @returns {Promise<Array>} Promise that resolves to the listener results
     */
    undo: function () {
      if (!this.canUndo) return Promise.resolve(this.value);
      this.future.push(deepClone(this.value));
      this.value = this.history.pop();
      return this.notifyListeners();
    },

    /**
     * Redo the last undone state change (if possible)
     * @returns {Promise<Array>} Promise that resolves to the listener results
     */
    redo: function () {
      if (!this.canRedo) return Promise.resolve(this.value);
      this.history.push(deepClone(this.value));
      this.value = this.future.pop();
      return this.notifyListeners();
    },

    /**
     * Boolean indicating if undo is possible
     */
    get canUndo() {
      return this.history.length > 0;
    },

    /**
     * Boolean indicating if redo is possible
     */
    get canRedo() {
      return this.future.length > 0;
    },

    /**
     * Adds a listener function to be called when state changes
     * @param {Function} listener - Callback function that receives the new state value
     * @returns {Function} Unsubscribe function to remove the listener
     *
     * @example
     * const unsubscribe = state.subscribe(newValue => {
     *     console.log('State changed:', newValue);
     * });
     *
     * // Later: cleanup subscription
     * unsubscribe();
     */
    subscribe: function (listener) {
      this.listeners.add(listener);
      return () => {
        this.listeners.delete(listener);
      };
    },

    /**
     * Internal method to notify all listeners of state changes
     * Executes all listener callbacks with the new state value
     * Handles errors in individual listeners to prevent chain breaking
     * @returns {Promise<Array>} Results from all listener callbacks
     */
    notifyListeners: function () {
      const promises = Array.from(this.listeners).map(async listener => {
        try {
          return await listener(this.value);
        } catch (error) {
          console.error('Listener error:', error);
          return null;
        }
      });

      return Promise.all(promises);
    },

    /**
     * Resets the state to its initial value
     * @returns {Promise<Array>} Promise that resolves to the listener results
     */
    clear: function () {
      this.history = [];
      this.future = [];
      return this[setKey](initialValue);
    },
  };

  return state;
};

export default _State;
